import { useState, useImperativeHandle, forwardRef } from "react";
import { useTranslation } from "react-i18next";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { CompanyIdentity } from "@/types/businessData";

export interface StepHandle {
  getData: () => unknown;
}

interface CompanyProfileStepProps {
  initialData?: CompanyIdentity;
  errorMsg: string | null;
}

const BUSINESS_MODELS = ["B2B", "B2C", "B2B2C", "D2C"] as const;

const INDUSTRY_KEYS = [
  "itTech",
  "saas",
  "financialServices",
  "healthLifeScience",
  "manufacturing",
  "constructionRealEstate",
  "consulting",
  "ecommerce",
  "groceryRetail",
  "education",
  "mediaAdvertising",
  "transportLogistics",
  "energy",
  "publicSector",
  "other",
] as const;

type IndustryKey = (typeof INDUSTRY_KEYS)[number];

const OTHER_INDUSTRY_KEY: IndustryKey = "other";

/** Legacy Swedish labels stored before industry keys were introduced. */
const LEGACY_INDUSTRY_TO_KEY: Record<string, IndustryKey> = {
  "IT & Tech": "itTech",
  SaaS: "saas",
  Finanstjänster: "financialServices",
  "Hälsa & Life Science": "healthLifeScience",
  Tillverkning: "manufacturing",
  "Bygg & Fastighet": "constructionRealEstate",
  Konsulttjänster: "consulting",
  "E-handel": "ecommerce",
  Utbildning: "education",
  "Media & Reklam": "mediaAdvertising",
  "Transport & Logistik": "transportLogistics",
  Energi: "energy",
  "Offentlig sektor": "publicSector",
  Annat: "other",
};

function normalizeIndustryValue(value: string): string {
  if ((INDUSTRY_KEYS as readonly string[]).includes(value)) return value;
  return LEGACY_INDUSTRY_TO_KEY[value] ?? value;
}

function isCustomIndustry(value: string): boolean {
  const normalized = normalizeIndustryValue(value);
  return (
    normalized !== "" &&
    !(INDUSTRY_KEYS as readonly string[]).includes(normalized)
  );
}

const CompanyProfileStep = forwardRef<StepHandle, CompanyProfileStepProps>(
  function CompanyProfileStep({ initialData, errorMsg }, ref) {
    const { t } = useTranslation("onboarding");
    const [formData, setFormData] = useState({
      description: initialData?.description ?? "",
      culture: initialData?.culture ?? "",
      businessModel: initialData?.businessModel ?? [],
      industry: normalizeIndustryValue(initialData?.industry ?? ""),
    });

    const industryIsCustom =
      formData.industry === OTHER_INDUSTRY_KEY ||
      isCustomIndustry(formData.industry);
    const [customIndustry, setCustomIndustry] = useState(() => {
      const raw = initialData?.industry ?? "";
      return isCustomIndustry(raw) ? raw : "";
    });

    useImperativeHandle(ref, () => ({
      getData: (): CompanyIdentity => ({
        ...formData,
        industry: industryIsCustom ? customIndustry : formData.industry,
      }),
    }));

    const toggleBusinessModel = (model: string) => {
      setFormData((prev) => {
        const current = prev.businessModel;
        const next = current.includes(model)
          ? current.filter((m) => m !== model)
          : [...current, model];
        return { ...prev, businessModel: next };
      });
    };

    const handleIndustryChange = (value: string) => {
      setFormData((prev) => ({ ...prev, industry: value }));
      if (value !== OTHER_INDUSTRY_KEY) {
        setCustomIndustry("");
      }
    };

    return (
      <div className="space-y-6">
        <p className="text-[15px] leading-relaxed text-muted-foreground">
          {t("companyProfile.description")}
        </p>

        {errorMsg && (
          <div
            role="alert"
            className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-sm font-semibold text-destructive"
          >
            {errorMsg}
          </div>
        )}

        <div className="space-y-5">
          <div className="space-y-2">
            <Label>{t("companyProfile.businessModelLabel")}</Label>
            <div className="flex flex-wrap gap-4">
              {BUSINESS_MODELS.map((model) => (
                <label
                  key={model}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <Checkbox
                    checked={formData.businessModel.includes(model)}
                    onCheckedChange={() => toggleBusinessModel(model)}
                  />
                  <span className="text-sm">{model}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="industry">{t("companyProfile.industryLabel")}</Label>
            <Select
              value={industryIsCustom ? OTHER_INDUSTRY_KEY : formData.industry}
              onValueChange={handleIndustryChange}
            >
              <SelectTrigger id="industry" className="h-11">
                <SelectValue placeholder={t("companyProfile.industryPlaceholder")} />
              </SelectTrigger>
              <SelectContent>
                {INDUSTRY_KEYS.map((key) => (
                  <SelectItem key={key} value={key}>
                    {t(`companyProfile.industries.${key}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {industryIsCustom && (
              <Input
                value={customIndustry}
                onChange={(e) => setCustomIndustry(e.target.value)}
                placeholder={t("companyProfile.industryCustomPlaceholder")}
                className="h-11 mt-2"
              />
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">{t("companyProfile.descriptionLabel")}</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              placeholder={t("companyProfile.descriptionPlaceholder")}
              rows={4}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="culture">{t("companyProfile.cultureLabel")}</Label>
            <p className="text-xs text-muted-foreground">
              {t("companyProfile.cultureHint")}
            </p>
            <Textarea
              id="culture"
              value={formData.culture}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, culture: e.target.value }))
              }
              placeholder={t("companyProfile.culturePlaceholder")}
              rows={4}
            />
          </div>
        </div>
      </div>
    );
  },
);

export default CompanyProfileStep;
